import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  Connection1cFormDto,
  type Connection1cFormInterface
} from '@zaoblako/shared/modules/integration/dtos/connection-1c/connection-1c-form.dto';
import {
  Connection1cCredentialsDto,
  type Connection1cCredentialsInterface
} from '@zaoblako/shared/modules/integration/dtos/connection-1c/connection-1c-credentials.dto';
import type { Connection1cFindInterface } from '@zaoblako/shared/modules/integration/dtos/connection-1c/connection-1c-find.dto';
import { BasisSystem1cService } from '@zaoblako/web/core/services/basis/basis-system-1c.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { Integration1cService } from '@zaoblako/web/core/services/integration/integration1c.service';
import { Connection1cConsoleModalComponent } from '@zaoblako/web/modules/company/dumbs/connection1c-console-modal/connection1c-console-modal.component';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { GoBackButtonComponent } from '@zaoblako/web/shared/components/go-back-button/go-back-button.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { AuthService } from '@zaoblako/web/core/services/security/auth.service';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { PasswordModule } from 'primeng/password';
import { Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-database1c-form',
  templateUrl: './database1c-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    PasswordModule,
    ButtonModule,
    CheckboxModule,
    FormsModule,
    GoBackButtonComponent,
    SpinnerComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class Database1cFormComponent extends BaseFormComponent<Connection1cFormInterface> implements OnInit, OnDestroy {
  @Input() public readonly index: number;
  public engine: 'class-transformer' | 'serde' = 'serde';
  public database: Connection1cFormInterface = <Connection1cFormInterface>{};
  public dto = Connection1cFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public connection: boolean;
  public connectionLoading: boolean;

  public showPassword = false;

  public loadingDownload = false;
  public isDevConsoleAvailable = false;

  private ref: DynamicDialogRef;

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly integration1cService: Integration1cService,
    private readonly basisSystem1cService: BasisSystem1cService,
    private readonly authService: AuthService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly confirmService: ConfirmDialogService,
    private readonly dialogService: DialogService,
    private readonly errorService: ErrorService
  ) {
    super();
  }

  public ngOnInit() {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    this.isDevConsoleAvailable = this.authService?.currentProfile?.isSuperAdmin;

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public ngOnDestroy() {
    this.ref?.close();
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.integration1cService
      .findById<Connection1cFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (database) => {
          this.database = database as any;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveDatabase() {
    if (this.database.disabled === null || this.database.disabled === undefined) {
      this.database.disabled = false;
    }

    if (this.checkValidate(this.database).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.integration1cService
          .update<Connection1cFormInterface, Connection1cFindInterface>(this.database)
          .subscribe({
            next: (database) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`База "${this.database?.name}" обновлена`);
              this.formStoreService.saveNext(database, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.integration1cService.create<Connection1cFindInterface>(this.database).subscribe({
          next: (database) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('База создана');
            this.formStoreService.saveNext(database, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteDatabase() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить базу "${this.database?.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.integration1cService
          .deleteById(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe(() => {
            this.errorService.addSuccessMessage(`База "${this.database?.name}" удалена`);
            this.formStoreService.deleteNext(this.index);
          });
      }
    });
  }

  public testConnection(): void {
    const validationResult = Connection1cCredentialsDto.validateSyncSafe(this.database);
    this.errors = validationResult[1] ? null : validationResult[1];

    if (this.errors) {
      return;
    }

    this.connectionLoading = true;
    const connectionPayload: Connection1cCredentialsInterface = {
      host: this.database.host,
      database: this.database.database,
      login: this.database.login,
      password: this.database.password,
      secure: this.database.secure
    };

    this.integration1cService.testConnection(connectionPayload).subscribe({
      next: (result) => {
        this.connectionLoading = false;
        this.connection = result.success;

        if (this.connection === false) {
          console.error(result.errors);
          this.errorService.addCustomError('Ошибка подключения', result.errors.join(', '));
        }
        this.cdRef.markForCheck();
      },
      error: () => {
        this.connectionLoading = false;
        this.cdRef.markForCheck();
      }
    });
  }

  public downloadExtension() {
    this.loadingDownload = true;
    this.cdRef.markForCheck();

    this.basisSystem1cService.download().subscribe((data) => {
      const file = new Blob([data], { type: 'application/octet-stream' });
      let url = URL.createObjectURL(file);
      let a = document.createElement('a');
      document.body.appendChild(a);
      a.setAttribute('style', 'display: none');
      a.href = url;
      a.download = 'ЗаФинансы.cfe';
      a.click();
      URL.revokeObjectURL(url);
      a.remove();
      this.loadingDownload = false;
      this.cdRef.markForCheck();
    });
  }

  public openConsoleModal() {
    this.ref = this.dialogService.open(Connection1cConsoleModalComponent, {
      header: 'Консоль разработчика',
      contentStyle: { overflow: 'hidden' },
      styleClass: 'xl:w-4 lg:w-6 md:w-8 sm:w-11 w-full',
      baseZIndex: 10000,
      data: {
        databaseId: this.database['id']
      }
    });
  }
}
